import React, { FC, ReactNode } from 'react';
import { TypographyWithElipsis } from '@components/TypographyElipsis';
import { Box, MenuItem, Stack, Typography } from '@mui/material';
import { categorySummary } from '@store/zustand/model-filters';
import { ArrowBigRightIcon } from '@utils/iconsDefs';

type Props = {
  icon: ReactNode;
  label: string;
  values: string[];
  labels: Record<string, string>;
  onClick: () => void;
  badge?: number;
  selected?: boolean;
};

/**
 * One category row in the Filters root popover, and one criteria row in a
 * CIA category's criteria list.
 *
 * The secondary line is the AC3.2 summary ladder rather than a value list.
 * The trailing marker is one of two: with no `badge`, the dot that marks a
 * root category carrying a selection; with a `badge`, a count of the
 * answers ticked under a criteria, shown while it is above zero and drawn
 * like the funnel's own badge. The row owns no selection state: it renders
 * what it is given and reports the click, so the panel above it decides
 * what opens. `selected` marks the criteria whose values are open beside
 * the list, on the row's hover background rather than MUI's primary tint,
 * which is how the design draws the open row. The leading slot keeps its
 * 20px width when no icon is
 * supplied, so the labels of a mixed list stay aligned. Row padding, gap
 * and hover come from the `MuiMenuItem` theme override, which already
 * matches the Figma row.
 *
 * The category name is regular weight, not the semibold the panel headers
 * use: in the design the weight is what separates a heading from the list
 * beneath it.
 */
const FilterCategoryRow: FC<Props> = ({
  icon,
  label,
  values,
  labels,
  onClick,
  badge,
  selected = false,
}) => {
  const hasSelection = values.length > 0;

  return (
    <MenuItem
      onClick={onClick}
      selected={selected}
      sx={{ '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'grey.400' } }}
    >
      <Box display="flex" width={20} height={20} flexShrink={0}>
        {icon}
      </Box>

      <Stack flex={1} minWidth={0}>
        <Typography variant="body1" color="grey.1000" noWrap>
          {label}
        </Typography>

        <TypographyWithElipsis
          variant="body2"
          color="grey.600"
          tooltipPlacement="bottom"
        >
          {categorySummary(values, labels, label)}
        </TypographyWithElipsis>
      </Stack>

      {badge === undefined && hasSelection && (
        <Box
          aria-hidden
          data-testid="filter-category-selected-dot"
          flexShrink={0}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'primary.main',
          }}
        />
      )}

      {badge !== undefined && badge > 0 && (
        <Stack
          data-testid="filter-category-selected-badge"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            bgcolor: 'primary.main',
          }}
        >
          <Typography variant="body2" color="grey.1000">
            {badge}
          </Typography>
        </Stack>
      )}

      <Box display="flex" width={20} height={20} flexShrink={0}>
        <ArrowBigRightIcon width={20} height={20} />
      </Box>
    </MenuItem>
  );
};

export default FilterCategoryRow;
